/**
 * Task #1677 — single source of truth for the "where is this trip happening"
 * string used across the renter trip detail banner, the address row under
 * the pickup/dropoff times, the host trip detail page, and (mirrored
 * server-side in api-server/src/lib/trip-location.ts) the email and in-app
 * reminder copy.
 *
 * Returns a structured value so callers can choose between a short form
 * ("in Taunton" / "delivered to 10 Downing St") for inline use after the
 * car title, and a long form ("10 Downing St, London") for standalone
 * rendering in an address row.
 */
export type TripLocationKind =
  | "self_pickup"
  | "event_address"
  | "delivery_address"
  | "delivery_airport"
  | "delivery_fallback";

export interface TripLocationInput {
  is_delivery?: boolean | null;
  delivery_address?: string | null;
  airport_code?: string | null;
  airport_name?: string | null;
  pickup_latitude?: number | string | null;
  pickup_longitude?: number | string | null;
  /** Task #5789 — the customer's event/pickup address (legacy "Event From"),
   *  stored on the trip's `route_from`. Surfaced for chauffeured/event
   *  bookings instead of the car's home city. */
  route_from?: string | null;
  /** Task #5789 — rental classification used to detect chauffeured/event
   *  bookings that carry an explicit event address. */
  rental_type?: string | null;
  trip_type?: string | number | null;
  event_type?: string | null;
  cars?: {
    city?: string | null;
    state?: string | null;
    address?: string | null;
  } | null;
}

export interface TripLocationSummary {
  kind: TripLocationKind;
  /** True when the trip was checked out as delivery. */
  isDelivery: boolean;
  /** Resolved street address for delivery trips, when known. */
  address: string | null;
  /** Task #5789 — the customer's event/pickup address for chauffeured/event
   *  bookings (from `route_from`), when known. */
  eventAddress: string | null;
  /** Airport code (e.g. "LHR") when the trip is airport delivery. */
  airportCode: string | null;
  /** Pickup coords if known (used by the page to one-shot reverse geocode). */
  pickupLat: number | null;
  pickupLng: number | null;
  /** Car's home city for self-pickup labelling. */
  carCity: string | null;
}

function asNum(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

/**
 * Task #5789 — join location parts (city / state / address fragments) into a
 * single "A, B" label while dropping blank, whitespace-only, and the literal
 * strings "null"/"undefined" that legacy rows leak when an empty column is
 * string-concatenated. Yields "Huddinge" when state is missing and
 * "Huddinge, MA" when both exist — never "Huddinge, null".
 */
export function formatLocationLabel(...parts: Array<string | null | undefined>): string {
  return parts
    .map((p) => String(p ?? "").trim())
    .filter((p) => p.length > 0 && p.toLowerCase() !== "null" && p.toLowerCase() !== "undefined")
    .join(", ");
}

/** Task #5789 — true for chauffeured / event bookings, where `route_from`
 *  holds the customer's event address rather than a delivery target. */
function isChauffeurOrEventBooking(trip: TripLocationInput | null | undefined): boolean {
  const r = String(trip?.rental_type || "").toLowerCase();
  const tt = trip?.trip_type;
  return (
    r === "chauffeured" ||
    r === "chauffeur" ||
    r === "event" ||
    String(tt) === "1" ||
    tt === "chauffeured"
  );
}

export function getTripLocationSummary(trip: TripLocationInput | null | undefined): TripLocationSummary {
  const isDelivery = !!trip?.is_delivery
    || !!(trip?.airport_code && String(trip.airport_code).trim())
    || !!(trip?.delivery_address && String(trip.delivery_address).trim());

  const address = trip?.delivery_address && String(trip.delivery_address).trim()
    ? String(trip.delivery_address).trim()
    : null;
  const airportCode = trip?.airport_code && String(trip.airport_code).trim()
    ? String(trip.airport_code).trim().toUpperCase()
    : null;
  const pickupLat = asNum(trip?.pickup_latitude);
  const pickupLng = asNum(trip?.pickup_longitude);
  const carCity = trip?.cars?.city ? String(trip.cars.city) : null;

  // Task #5789 — a chauffeured/event booking with a stored event address
  // (route_from) has an explicit location the customer provided. Surface it
  // instead of classifying the trip as self-pickup and falling back to the
  // car's home city.
  const routeFrom = trip?.route_from && String(trip.route_from).trim()
    ? String(trip.route_from).trim()
    : null;
  const eventAddress = routeFrom && isChauffeurOrEventBooking(trip) ? routeFrom : null;

  let kind: TripLocationKind;
  if (eventAddress) kind = "event_address";
  else if (!isDelivery) kind = "self_pickup";
  else if (address) kind = "delivery_address";
  else if (airportCode) kind = "delivery_airport";
  else kind = "delivery_fallback";

  return { kind, isDelivery, address, eventAddress, airportCode, pickupLat, pickupLng, carCity };
}

export interface TripLocationLabelOptions {
  /** Translator function compatible with i18next's `t`. */
  t: (key: string, opts?: Record<string, unknown>) => string;
  /** Optional address resolved at render time from coords (one-shot reverse
   *  geocode) — used as a fallback for `delivery_fallback` when only coords
   *  were captured. */
  resolvedAddress?: string | null;
  /** Optional human airport name (e.g. "Heathrow"). When present we render
   *  "{name} ({code})" otherwise just the code. */
  airportName?: string | null;
}

/**
 * Short, inline-friendly form intended to follow a car title:
 *   self pickup       → "in Taunton"           (or "" when no city)
 *   delivery+address  → "delivered to 10 Downing St"
 *   delivery+airport  → "delivered to LHR"
 *   delivery+fallback → "delivered to your address"
 */
export function formatTripLocationShort(
  summary: TripLocationSummary,
  { t, resolvedAddress, airportName }: TripLocationLabelOptions,
): string {
  switch (summary.kind) {
    case "self_pickup":
      return summary.carCity ? t("trip.in_city", { city: summary.carCity }) : "";
    case "event_address":
      // Task #5789 — reuse the "in {city}" phrasing with the event address so
      // the banner subtitle reads "in Haninge, Sweden" for a chauffeured/event
      // booking instead of the car's garage city.
      return summary.eventAddress ? t("trip.in_city", { city: summary.eventAddress }) : "";
    case "delivery_address":
      return t("trip.delivered_to_address", { address: summary.address });
    case "delivery_airport":
      return t("trip.delivered_to_airport", {
        airport: airportName ? `${airportName} (${summary.airportCode})` : summary.airportCode,
      });
    case "delivery_fallback":
      if (resolvedAddress) {
        return t("trip.delivered_to_address", { address: resolvedAddress });
      }
      return t("trip.delivered_to_your_address_short");
  }
}

/**
 * Long, standalone form for a labelled "address" row. Falls back to the
 * generic "We'll deliver to the address you provided at checkout" only
 * when delivery is selected but no address / airport / coords are known.
 */
export function formatTripLocationLong(
  summary: TripLocationSummary,
  carAddress: string,
  { t, resolvedAddress, airportName }: TripLocationLabelOptions,
): string {
  switch (summary.kind) {
    case "self_pickup":
      return carAddress;
    case "event_address":
      // Task #5789 — the customer's stored event address is the Location for a
      // chauffeured/event booking; fall back to the car address only if empty.
      return summary.eventAddress || carAddress;
    case "delivery_address":
      return summary.address || carAddress;
    case "delivery_airport":
      return airportName ? `${airportName} (${summary.airportCode})` : (summary.airportCode || "");
    case "delivery_fallback":
      return resolvedAddress || t("trip.delivery_to_your_address");
  }
}
